import { presetsFor } from '../../../commands';
import type { ViewerShell } from '../../../shell';
import { isImageSeries } from '../../../study';
import { type ActionContext, actionLabel, isSupportedAction, runAction } from '../actions';
import type { AssistantAction } from '../api';
import { conversationStudy } from '../context';
import { aiLimitText } from '../errors';
import { withRetry } from '../transcribe-retry';
import { MAX_AUDIO_BYTES, VoiceRecorder, voiceSupported } from '../voice';
import type { ViewerState } from './api';
import { el, resultCard, type ToolDeps } from './dom';

/**
 * Voice commands: "Comando" beside the microphone. Pressed, it records; pressed again, the clip is
 * transcribed by ris and sent to `/assistant/command`, and the actions it answers run through the
 * Asistente's closed action map (the same handlers as an answer's buttons, so nothing outside that
 * list can run and insert_report still asks before copying). Hidden where dictation cannot work.
 */

export const COMMAND_TEXT = {
  button: 'Comando',
  stop: 'Ejecutar',
  label: 'Dar una orden de voz al visor',
  stopLabel: 'Terminar la orden de voz y ejecutarla',
  working: 'Procesando…',
  title: 'Orden de voz',
  heard: (text: string) => `«${text}»`,
  ran: 'Acciones ejecutadas:',
  none: 'No hay ninguna acción del visor para esa orden.',
  micError: 'No fue posible acceder al micrófono.',
  empty: 'No se grabó audio.',
  tooLong: 'La grabación es demasiado larga.',
  failed: 'No fue posible ejecutar la orden de voz.',
} as const;

/** At most this many actions run from one command. */
export const MAX_COMMAND_ACTIONS = 4;

/** What the viewer tells ris about itself so a command like "siguiente serie" can be resolved. */
export function viewerState(shell: ViewerShell): ViewerState {
  const vp = shell.activeViewport();
  const index = vp?.currentIndex();
  const study = shell.studies().find((s) => s.studyInstanceUID === conversationStudy(shell));
  return {
    slice: index === undefined ? 0 : index + 1,
    count: vp?.currentStack?.imageIds.length ?? 0,
    series: (study?.series ?? []).filter(isImageSeries).map((s) => s.description || `Serie ${s.seriesNumber}`),
    presets: presetsFor(shell).map((p) => p.key),
  };
}

/** Action types that each start a paid model run: one spoken command starts at most one of them. */
const PAID_ACTIONS = new Set(['run_algorithm']);

/**
 * The actions one command may run, in order: the supported ones (unknown types skipped), at most one
 * paid run (a model that repeats run_algorithm must not multiply the bill without a click), at most
 * MAX_COMMAND_ACTIONS in all. Pure, so the unit spec checks it without a browser.
 */
export function commandActions(actions: AssistantAction[], limit = MAX_COMMAND_ACTIONS): AssistantAction[] {
  let paid = 0;
  return actions.filter((action) => {
    if (!isSupportedAction(action)) return false;
    if (!PAID_ACTIONS.has(action.type)) return true;
    paid += 1;
    return paid === 1;
  }).slice(0, limit);
}

/**
 * Runs, in order, the actions `commandActions` keeps; answers what ran. Also "Preparar estudio"
 * (radiologist tools), which allows a longer plan.
 */
export async function runCommandActions(ctx: ActionContext, actions: AssistantAction[], limit = MAX_COMMAND_ACTIONS): Promise<AssistantAction[]> {
  const ran: AssistantAction[] = [];
  for (const action of commandActions(actions, limit)) {
    await runAction(ctx, action);
    ran.push(action);
  }
  return ran;
}

function showResult(deps: ToolDeps, study: string, heard: { text: string; reply: string }, ran: AssistantAction[]): void {
  const { text, reply } = heard;
  const { card, body } = resultCard('command', COMMAND_TEXT.title);
  body.appendChild(el('div', 'assistant-command-heard', COMMAND_TEXT.heard(text)));
  if (reply) body.appendChild(el('div', 'assistant-msg-text', reply));
  if (ran.length === 0) {
    body.appendChild(el('div', 'assistant-tool-note', COMMAND_TEXT.none));
  } else {
    body.appendChild(el('div', 'assistant-tool-note', COMMAND_TEXT.ran));
    const list = el('ul', 'assistant-command-actions');
    ran.forEach((a) => {
      const item = el('li', 'assistant-command-action', actionLabel(a));
      item.dataset.action = a.type;
      list.appendChild(item);
    });
    body.appendChild(list);
  }
  // The card belongs to the conversation the command was spoken in, even if an action moved it.
  deps.panel.appendResult(card, study);
}

/** Transcript → command → actions. Exported for the typed path and the tests. */
export async function runCommand(deps: ToolDeps, text: string): Promise<void> {
  const study = conversationStudy(deps.shell);
  const answer = await deps.api.command(study, text, viewerState(deps.shell));
  const ran = await runCommandActions({ shell: deps.shell, api: deps.assistantApi }, answer.actions);
  showResult(deps, study, { text, reply: answer.reply }, ran);
}

export class CommandButton {
  readonly element: HTMLButtonElement | null;
  private readonly recorder = new VoiceRecorder();
  private working = false;

  constructor(private readonly deps: ToolDeps) {
    if (!voiceSupported()) {
      this.element = null;
      return;
    }
    const b = el('button', 'assistant-btn assistant-command');
    b.type = 'button';
    b.addEventListener('click', () => void this.toggle());
    this.element = b;
    this.recorder.onAutoStop = (clip) => void this.process(clip);
    this.render();
  }

  private render(label?: string): void {
    const b = this.element;
    if (!b) return;
    const recording = this.recorder.recording;
    b.textContent = label ?? (recording ? COMMAND_TEXT.stop : COMMAND_TEXT.button);
    b.setAttribute('aria-label', recording ? COMMAND_TEXT.stopLabel : COMMAND_TEXT.label);
    b.setAttribute('aria-pressed', String(recording));
    b.classList.toggle('recording', recording);
    b.disabled = this.working;
  }

  private async toggle(): Promise<void> {
    if (this.working) return;
    if (this.recorder.recording) return void (await this.process(await this.recorder.stop()));
    try {
      await this.recorder.start();
    } catch (error) {
      console.warn('[viewer] microphone unavailable', error);
      this.deps.shell.toast(COMMAND_TEXT.micError, 'error');
    }
    this.render();
  }

  private clipProblem(clip: Blob): string | null {
    if (clip.size === 0) return COMMAND_TEXT.empty;
    return clip.size > MAX_AUDIO_BYTES ? COMMAND_TEXT.tooLong : null;
  }

  private async process(clip: Blob): Promise<void> {
    const problem = this.clipProblem(clip);
    if (problem) {
      this.deps.shell.toast(problem, 'warning');
      return this.render();
    }
    this.working = true;
    this.render(COMMAND_TEXT.working);
    try {
      const study = conversationStudy(this.deps.shell);
      // Same timeout + retry on network / 5xx as dictation. A command is not kept after that: replayed
      // later it would act on whatever the viewer shows then, so the failure is said and that is all.
      const text = (await withRetry((signal) => this.deps.assistantApi.transcribe(study, clip, signal))).trim();
      if (text) await runCommand(this.deps, text);
      else this.deps.shell.toast(COMMAND_TEXT.empty, 'warning');
    } catch (error) {
      console.warn('[viewer] voice command failed', error);
      this.deps.shell.toast(aiLimitText(error) ?? COMMAND_TEXT.failed, 'error');
    } finally {
      this.working = false;
      this.render();
    }
  }

  /** The tab was hidden: drop a recording in progress. */
  cancel(): void {
    if (!this.recorder.recording) return;
    this.recorder.cancel();
    this.render();
  }
}
